import Vue from 'vue';
import Vuex from 'vuex';
import {
  loginUser,
  loginSsoUser,
  logoutUser,
  loginUserUpSide,
  loginTenantCenter,
  logoutTenantCenterUser
} from '../services/api/auth';
import PreferenceService from '../services/PreferenceService';
import UrbanRenewalDataService from '../services/UrbanRenewalDataService';
import UrbanRenewalSelectService from '../services/UrbanRenewalSelectService';
import LicenseService from '../services/LicenseService';

// 挑選登入自動帶入的預設建案:
// 1. 優先「未過截止日」者(無截止日視為未過期);其中依「開始日最早」取第一個
//    → 代表當前最該處理的進行中建案。
// 2. 若全部都已過截止日,退而帶「截止日最近剛過」者(截止日最大)。
// 註:日期為 Django DateField 序列化的 ISO 字串(YYYY-MM-DD),可直接字串比較。
function pickDefaultProjectId(projects) {
  if (!projects || projects.length === 0) return null;
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const notExpired = projects.filter(p => !p.selection_deadline || p.selection_deadline >= todayStr);
  if (notExpired.length > 0) {
    // 依開始日由早到晚;無開始日排最後
    const sorted = [...notExpired].sort((a, b) => {
      const sa = a.selection_start || '9999-12-31';
      const sb = b.selection_start || '9999-12-31';
      return sa < sb ? -1 : (sa > sb ? 1 : 0);
    });
    return sorted[0].id;
  }
  // 全部過期:帶截止日最近剛過的(截止日最大)
  const byDeadlineDesc = [...projects].sort((a, b) => {
    const da = a.selection_deadline || '';
    const db = b.selection_deadline || '';
    return da < db ? 1 : (da > db ? -1 : 0);
  });
  return byDeadlineDesc[0].id;
}

const urbanRenewalAdmin = {
  namespaced: true,
  state: {
    projects: [],
    currentProjectId: null,
    logoUrl: '',
  },
  mutations: {
    SET_PROJECTS(state, projects) {
      state.projects = projects;
    },
    SET_CURRENT_PROJECT_ID(state, id) {
      state.currentProjectId = id;
      // 記住選到的建案，重整後還原（登出時會清除）
      try {
        if (id == null) window.localStorage.removeItem('ur_admin_project_id');
        else window.localStorage.setItem('ur_admin_project_id', String(id));
      } catch (_) { /* localStorage 不可用時略過 */ }
    },
    SET_LOGO_URL(state, url) {
      state.logoUrl = url || '';
    },
  },
  actions: {
    async fetchProjects({ commit, state }) {
      try {
        const res = await UrbanRenewalDataService.getProjects();
        const projects = res.data || [];
        commit('SET_PROJECTS', projects);
        if (state.currentProjectId === null && projects.length > 0) {
          // 優先還原上次選的建案（存在於清單才用），否則回退預設（截止日最近）
          let saved = null;
          try { saved = Number(window.localStorage.getItem('ur_admin_project_id')); } catch (_) { saved = null; }
          const exists = saved && projects.some(p => p.id === saved);
          commit('SET_CURRENT_PROJECT_ID', exists ? saved : pickDefaultProjectId(projects));
        }
      } catch (_) {
        // 保留預設空值
      }
    },
    async fetchLogo({ commit }) {
      try {
        const res = await UrbanRenewalDataService.getLogo();
        commit('SET_LOGO_URL', res.data === '' ? '' : res.data);
      } catch (_) {
        // 保留預設（空 → 元件 fallback 到 UPGPT.png）
      }
    },
  },
};

// Urban Renewal Select (前台選配) — token/建案/基本資料/狀態
// 登入本身已在 Login.vue（FE-P01）直接呼叫 service，此 module 不重覆做 login action。
const urbanRenewalSelect = {
  namespaced: true,
  state: {
    project: null,          // {project_id, project_name, selection_start, selection_deadline}
    owners: [],
    allocatedValue: 0,
    selectableValue: 0,
    lastUpdated: null,        // 地主草稿存檔時間（供狀態徽章判斷已儲存）
    statsUpdated: null,       // Admin 手動登打最後時間（右面板「本統計結果最後更新時間」）
    grid: { housing: [], parking: [] },
    selectedHousing: [],
    selectedParking: [],
    checkedOwnerIds: [],
    overDialogVisible: false,
    dirty: false,           // 有未儲存的變更 → 匯出擋下、右上狀態顯示「選配試算中」
    aiSupport: null,        // AI 客服（UpChat webchat）設定 { enabled, url, token }；來自 select/me
  },
  mutations: {
    SET_PROJECT(state, p) { state.project = p; },
    SET_ME(state, me) {
      state.owners = me.owners || [];
      state.allocatedValue = me.allocated_value || 0;
      state.selectableValue = me.selectable_value || 0;
      state.lastUpdated = me.last_updated || null;
      state.statsUpdated = me.stats_updated || null;
      state.aiSupport = me.ai_support || null;
      state.checkedOwnerIds = (me.owners || []).map(o => o.id);   // 預設全勾（決策 #7）
    },
    SET_GRID(state, g) { state.grid = { housing: g.housing || [], parking: g.parking || [] }; },
    SET_LAST_UPDATED(state, v) { state.lastUpdated = v; },
    TOGGLE_OWNER(state, id) {
      const i = state.checkedOwnerIds.indexOf(id);
      if (i >= 0) state.checkedOwnerIds.splice(i, 1); else state.checkedOwnerIds.push(id);
      state.dirty = true;
    },
    ADD_HOUSING(state, unit) {
      if (!state.selectedHousing.some(u => u.id === unit.id)) { state.selectedHousing.push(unit); state.dirty = true; }
    },
    REMOVE_HOUSING(state, id) {
      state.selectedHousing = state.selectedHousing.filter(u => u.id !== id);
      state.dirty = true;
    },
    ADD_PARKING(state, unit) {
      if (!state.selectedParking.some(u => u.id === unit.id)) { state.selectedParking.push(unit); state.dirty = true; }
    },
    REMOVE_PARKING(state, id) {
      state.selectedParking = state.selectedParking.filter(u => u.id !== id);
      state.dirty = true;
    },
    REPLACE_HOUSING(state, { oldId, unit }) {   // 原地替換：改選時維持列順序
      const i = state.selectedHousing.findIndex(u => u.id === oldId);
      if (i === -1) { if (!state.selectedHousing.some(u => u.id === unit.id)) state.selectedHousing.push(unit); }
      else state.selectedHousing.splice(i, 1, unit);
      state.dirty = true;
    },
    REPLACE_PARKING(state, { oldId, unit }) {
      const i = state.selectedParking.findIndex(u => u.id === oldId);
      if (i === -1) { if (!state.selectedParking.some(u => u.id === unit.id)) state.selectedParking.push(unit); }
      else state.selectedParking.splice(i, 1, unit);
      state.dirty = true;
    },
    CLEAR_SELECTIONS(state) {
      state.selectedHousing = [];
      state.selectedParking = [];
      state.dirty = true;
    },
    SET_SELECTIONS(state, { housing, parking, checkedOwnerIds }) {   // 供 FE-P07 還原
      state.selectedHousing = housing || [];
      state.selectedParking = parking || [];
      if (checkedOwnerIds) state.checkedOwnerIds = checkedOwnerIds;
      state.dirty = false;   // 還原自已存 draft → 與後端一致，非未存變更
    },
    SET_DIRTY(state, v) { state.dirty = v; },
    SET_OVER_DIALOG(state, v) { state.overDialogVisible = v; },
  },
  getters: {
    checkedOwners: (s) => s.owners.filter(o => s.checkedOwnerIds.includes(o.id)),
    allocatedTotal: (s, g) => g.checkedOwners.reduce((t, o) => t + (o.allocated_value || 0), 0),
    selectableTotal: (s, g) => g.checkedOwners.reduce((t, o) => t + (o.selectable_value || 0), 0),
    housingTotal: (s) => s.selectedHousing.reduce((t, u) => t + (u.total_price || 0), 0),
    parkingTotal: (s) => s.selectedParking.reduce((t, u) => t + (u.value || 0), 0),
    actualTotal: (s, g) => g.housingTotal + g.parkingTotal,
    makeup: (s, g) => g.selectableTotal - g.actualTotal,               // Case A
    makeupStatus: (s, g) => (g.makeup > 0 ? 'refund' : (g.makeup < 0 ? 'pay' : 'none')),
    isOverSelect: (s, g) => g.allocatedTotal > 0 && g.actualTotal > g.allocatedTotal * 1.1,  // FE-P06 用
  },
  actions: {
    initProject({ commit }) { commit('SET_PROJECT', UrbanRenewalSelectService.getProject()); },
    async fetchMe({ commit }) { const res = await UrbanRenewalSelectService.getMe(); commit('SET_ME', res.data); return res.data; },
    async fetchGrid({ commit }) { const res = await UrbanRenewalSelectService.getGrid(); commit('SET_GRID', res.data); return res.data; },
    loadDraft() { return UrbanRenewalSelectService.getDraft(); },
    saveDraft(_, payload) { return UrbanRenewalSelectService.saveDraft(payload); },
    async restoreDraft({ state, commit }) {   // FE-P07：進場還原上次 draft（選配 + checkbox）
      const res = await UrbanRenewalSelectService.getDraft();
      const d = res.data || {};
      const hMap = new Map(state.grid.housing.map(u => [u.id, u]));
      const pMap = new Map(state.grid.parking.map(u => [u.id, u]));
      const housing = (d.housing_unit_ids || []).map(id => hMap.get(id)).filter(Boolean);
      const parking = (d.parking_unit_ids || []).map(id => pMap.get(id)).filter(Boolean);
      // draft 有勾選成員 → 用之；無 draft → 維持 fetchMe 預設全勾
      const checkedOwnerIds = (d.checked_owner_ids && d.checked_owner_ids.length)
        ? d.checked_owner_ids
        : state.checkedOwnerIds;
      commit('SET_SELECTIONS', { housing, parking, checkedOwnerIds });
      commit('SET_LAST_UPDATED', d.last_updated || null);
    },
    addUnit({ getters, commit }, { type, unit }) {   // FE-P06：新增前 110% 硬擋，統一守門
      const val = type === 'housing' ? (unit.total_price || 0) : (unit.value || 0);
      if (getters.allocatedTotal > 0 && (getters.actualTotal + val) > getters.allocatedTotal * 1.1) {
        commit('SET_OVER_DIALOG', true);
        return false;   // 超選：不加入
      }
      if (type === 'housing') commit('ADD_HOUSING', unit); else commit('ADD_PARKING', unit);
      return true;
    },
    replaceUnit({ state, getters, commit }, { type, oldId, unit }) {   // 改選：原地替換，守門用「扣舊加新」；超選則舊的原地保留
      const list = type === 'housing' ? state.selectedHousing : state.selectedParking;
      const old = list.find(u => u.id === oldId);
      const valOf = u => (type === 'housing' ? (u && u.total_price || 0) : (u && u.value || 0));
      if (getters.allocatedTotal > 0 && (getters.actualTotal - valOf(old) + valOf(unit)) > getters.allocatedTotal * 1.1) {
        commit('SET_OVER_DIALOG', true);
        return false;
      }
      if (type === 'housing') commit('REPLACE_HOUSING', { oldId, unit }); else commit('REPLACE_PARKING', { oldId, unit });
      return true;
    },
  },
};

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    user: null,
    isLoggedIn: false,
    tenantUser: null,
    isTenantLoggedIn: false,
    isUpsideExtension: false,
    vectorBackend: 'azure_acs',
    webSearchEnabled: false,
    portalWebSearchEnabled: true,
    // 伺服器授權狀態。Portal Chat 與 Work Agent 共用這一份，兩邊不各自查詢。
    // 預設 VALID：查不到就顯示警告會讓載入中的畫面誤報授權問題。
    licenseStatusCode: 'VALID',
    licenseExpiryDate: null,
    licenseStopDate: null,
  },
  getters: {
    // 需要顯示紅色授權警告橫幅。
    hasLicenseWarning(state) {
      return !!state.licenseStatusCode && state.licenseStatusCode !== 'VALID';
    },
    // 需要停用送出與執行。只有 SUSPENDED 與 INVALID 會擋，EXPIRED_GRACE
    // 在寬限期內只顯示警告、功能照常。狀態碼的比對集中在這裡，
    // Portal Chat 與 Work Agent 兩個頁面不各自實作。
    isLicenseBlocking(state) {
      return ['SUSPENDED', 'INVALID'].includes(state.licenseStatusCode);
    },
  },
  mutations: {
    loginSuccess(state, userId) {
      state.user = userId;
      state.isLoggedIn = true;
    },
    logout(state) {
      state.user = null;
      state.isLoggedIn = false;
    },
    tenantLoginSuccess(state, tenantUser){
      state.tenantUser = tenantUser;
      state.isTenantLoggedIn = true;
    },
    tenantLogout(state){
      state.tenantUser = null;
      state.isTenantLoggedIn = false;
    },
    SET_IS_UPSIDE_EXTENSION(state, value){
      state.isUpsideExtension = value;
    },
    SET_VECTOR_BACKEND(state, value) {
      state.vectorBackend = value;
    },
    SET_WEB_SEARCH_ENABLED(state, value) {
      state.webSearchEnabled = value;
    },
    SET_PORTAL_WEB_SEARCH_ENABLED(state, value) {
      state.portalWebSearchEnabled = value;
    },
    SET_LICENSE_STATUS(state, { status_code, expiry_date, stop_date }) {
      state.licenseStatusCode = status_code;
      state.licenseExpiryDate = expiry_date;
      state.licenseStopDate = stop_date;
    },
  },
  actions: {
    login({
      commit
    }, {
      username,
      password,
      tenant,
      loginValiCode,
    }) {
      return loginUser(username, password, tenant, loginValiCode)
        .then(() => {
          commit({
            type: 'loginSuccess',
            username, tenant
          });
          return Promise.resolve();
        }).catch((error) => {
          commit({
            type: 'logout'
          });
          return Promise.reject(error);
        });
    },
    logout({
      commit
    }) {
      logoutUser();
      commit('logout');
    },
    loginUpGptUser({
      commit
    }, {
      username,
      password,
      tenant,
    }){
      return loginUser(username, password, tenant)
      .then(() => {
          commit({
            type: 'loginSuccess',
            username, tenant
          });
          return Promise.resolve();
      }).catch((error) => {
        commit({
          type: 'logout'
        });
        return Promise.reject(error);
      })
    },
    loginSsoUpGptUser({
      commit
    }, {
      tenant,
      loginValiCode,
      needRedirect,
    }){
      return loginSsoUser(tenant, loginValiCode, needRedirect)
      .then((redirectPath) => {
          commit({
            type: 'loginSuccess',
            tenant
          });
          return redirectPath
          // return Promise.resolve();
      }).catch((error) => {
        commit({
          type: 'logout'
        });
        return Promise.reject(error);
      })
    },
    // login for UpSide
    loginUpSide({
      commit
    }, {
      username,
      password,
      tenant,
      loginValiCode,
    }) {
      return loginUserUpSide(username, password, tenant, loginValiCode)
        .then((response) => {
          commit({
            type: 'loginSuccess',
            username, tenant
          });
          return response;
        }).catch((error) => {
          commit({
            type: 'logout'
          });
          return Promise.reject(error);
        });
    },
    // Tenant center Login
    loginTenantCenter(
      {commit}, {username, password,loginValiCode,}) {
      return loginTenantCenter(username, password, loginValiCode)
        .then((response) => {
          commit('tenantLoginSuccess',response.data);
          return response;
        }).catch((error) => {
          commit( 'tenantLogout');
          return Promise.reject(error);
        });
    },
    // Tenant center Logout
    logoutTenantCenterUser({
      commit
    }) {
      logoutTenantCenterUser();
      commit('tenantLogout');
    },
    setIsUpsideExension({
      commit
    },
    value
    ){
      commit('SET_IS_UPSIDE_EXTENSION', value)
    },
    async fetchOnPremSettings({ commit }) {
      try {
        const [vsRes, onpremRes, webSearchRes] = await Promise.all([
          PreferenceService.getVectorSearch(),
          PreferenceService.getOnPrem(),
          PreferenceService.getWebSearch(),
        ]);
        const backend = vsRes.data?.vector_backend || 'azure_acs';
        commit('SET_VECTOR_BACKEND', backend);
        const webSearch = onpremRes.data?.web_search_enabled;
        commit('SET_WEB_SEARCH_ENABLED', webSearch !== false);
        const portalWebSearch = webSearchRes.data;
        commit('SET_PORTAL_WEB_SEARCH_ENABLED', portalWebSearch !== "False");
      } catch (_) {
        // 保留預設值（azure_acs / webSearchEnabled: true）
      }
    },
    async fetchLicenseStatus({ commit }) {
      try {
        const res = await LicenseService.getStatus();
        const data = res.data || {};
        if (!data.status_code) {
          // 回應缺狀態碼視同查詢失敗，保留現有值。
          return;
        }
        commit('SET_LICENSE_STATUS', {
          status_code: data.status_code,
          expiry_date: data.expiry_date || null,
          stop_date: data.stop_date || null,
        });
      } catch (_) {
        // 查詢失敗（網路錯誤、伺服器無回應）時保留現有值，不顯示授權警告——
        // 網路抖動不該讓畫面誤報授權問題。
      }
    },
    // 送出／執行之前先問一次最新狀態，再回答「這次要不要擋」。
    // 使用者可能在畫面上停留很久，光靠進場那一次查詢會放行已停止服務的客戶。
    // 查詢失敗時 fetchLicenseStatus 保留現有值，因此這裡沿用上一次的判斷結果，
    // 不會因為網路抖動就擋住使用者——伺服器端的拒絕才是真正的防線。
    async refreshLicenseBlocking({ dispatch, getters }) {
      await dispatch('fetchLicenseStatus');
      return getters.isLicenseBlocking;
    },
  },
  modules: {
    urbanRenewalAdmin,
    urbanRenewalSelect,
  },
});